import type { RdScheduleAppointment } from "../../../../shared/rd-schedule";
import { firstNameOf } from "../../lib/rd-schedule-details";
import type { ActionAppointment, ActionClient } from "./actions/action-types";
import type { AppointmentDetailsActions } from "./appointment-details-body";
import type { AppointmentActionDialogsController } from "./appointment-action-dialogs";

// NO-876. How the details panel's buttons and a Week appointment map onto the
// action dialogs. Relative type-only imports, so `tsx --test` reads it.

export function toActionAppointment(
  appointment: RdScheduleAppointment
): ActionAppointment {
  return {
    id: appointment.id,
    startsAt: appointment.startsAt,
    endsAt: appointment.endsAt,
    clientName: appointment.client.name ?? "the Client",
    firstName: firstNameOf(appointment.client.name, "the Client"),
    typeName: appointment.appointmentType.name,
  };
}

export function toActionClient(appointment: RdScheduleAppointment): ActionClient {
  return {
    patientId: appointment.client.patientId,
    displayName: appointment.client.name ?? "Client",
    lastVisitAt: null,
  };
}

/**
 * The details panel's buttons as dialog openers. The sheet closes first so two
 * modal layers never stack; the dialog returns focus to the block's opener.
 */
export function detailsActions(input: {
  controller: AppointmentActionDialogsController;
  onClosePanel: () => void;
  onOpenVisitPage: (appointmentId: string) => void;
}): AppointmentDetailsActions {
  const { controller, onClosePanel, onOpenVisitPage } = input;

  const open =
    (action: (appointment: ActionAppointment) => void) =>
    (appointment: RdScheduleAppointment): void => {
      onClosePanel();
      action(toActionAppointment(appointment));
    };

  return {
    onBookNextVisit: (appointment) => {
      onClosePanel();
      controller.openNew(toActionClient(appointment), appointment.id);
    },
    onAddChartNote: (appointment) => onOpenVisitPage(appointment.id),
    onReschedule: open(controller.openReschedule),
    onEditDetails: open(controller.openEdit),
    onCancelOrMarkMissed: open(controller.openCancelOrMarkMissed),
    onDelete: open(controller.openDelete),
    onChangeStatus: open(controller.openCancelOrMarkMissed),
  };
}

/**
 * Reads of the week after an action. Healthie answers an action before the
 * webhook worker has updated the appointment the week reads, so the first read
 * can still show the old time; the follow-ups catch the change up.
 */
export const WEEK_REFRESH_DELAYS_MS = [0, 1500, 4000, 9000] as const;

export interface WeekRefresher {
  /** Reads now and again later, replacing any follow-ups still waiting. */
  run: () => void;
  cancel: () => void;
}

export function createWeekRefresher(
  refetch: () => void,
  timers: {
    set: (run: () => void, delayMs: number) => unknown;
    clear: (id: unknown) => void;
  } = {
    set: (run, delayMs) => setTimeout(run, delayMs),
    clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  }
): WeekRefresher {
  let pending: unknown[] = [];

  const cancel = (): void => {
    pending.forEach((id) => timers.clear(id));
    pending = [];
  };

  return {
    run: () => {
      cancel();
      refetch();
      pending = WEEK_REFRESH_DELAYS_MS.filter((delayMs) => delayMs > 0).map((delayMs) =>
        timers.set(refetch, delayMs)
      );
    },
    cancel,
  };
}
